import React, { useState } from 'react';
import type { UploadFile } from 'antd/es/upload/interface';
import classNames from 'classnames';
import { toast } from 'react-toastify';

import {
  trackUploadOrientationChoiceAccept,
  trackUploadOrientationReminderAccept,
  trackUploadOrientationModalReject,
} from '../../../analytics';

import { Spinner, Button, Modal, Checkbox } from '../../../components/general';
import { setEventMode } from '../../../services/api/events';

import { useProjectStore } from '../../../stores/project';
import { useUserStore } from '../../../stores/user';

import { VideoMode } from '../../../types';

interface Props {
  show: boolean;
  onHide: () => void;
  type: string;
  handleUpload: (files?: UploadFile[]) => void;
  rejectUpload: () => void;
  onShowShot: () => void;
}

const VideoModeModal: React.FC<Props> = (props) => {
  const { show, onHide, type, handleUpload, rejectUpload, onShowShot } = props;

  const { user } = useUserStore();
  const { eventId, currentProject, updateCurrentEvent } = useProjectStore();

  const videoMode = currentProject?.videoMode;
  const isPublic = currentProject?.isAssigneeAccess;

  const [isSaved, setIsSaved] = useState(false);
  const [isSettingMode, setIsSettingMode] = useState(false);

  const analyticsPage = type.replace(/-/g, '_');

  // accept upload & hide modal
  const handleAccept = (selectedMode: VideoMode) => {
    if (type === 'hl-reel' && !user?.dismissedShotReminder) onShowShot();
    else handleUpload();

    onHide();

    if (videoMode) trackUploadOrientationReminderAccept(analyticsPage, isSaved);
    else trackUploadOrientationChoiceAccept(analyticsPage, selectedMode);

    // save in localStorage that reminder is saved (won't show again)
    if (videoMode && isSaved) {
      let videoModeSaved = JSON.parse(
        localStorage.getItem('videoModeSaved') ?? '{}',
      );
      if (typeof videoModeSaved !== 'object') {
        videoModeSaved = {};
      }
      videoModeSaved[eventId] = true;
      localStorage.setItem('videoModeSaved', JSON.stringify(videoModeSaved));
    }
  };

  // reject upload & hide modal
  const handleReject = () => {
    rejectUpload();
    onHide();

    trackUploadOrientationModalReject(analyticsPage, !!videoMode);
  };

  // if mode not selected yet – save it in localStorage
  const onClickMode = async (mode: VideoMode) => {
    if (!videoMode) {
      setIsSettingMode(true);

      const data = { eventId, videoMode: mode };

      const { success } = await setEventMode(data, isPublic);

      await updateCurrentEvent();

      setIsSettingMode(false);

      if (success) handleAccept(mode);
      else {
        toast.error('Something went wrong. Try again later');
        handleReject();
      }
    }
  };

  const renderModeIcon = (orientation: VideoMode, isReminder?: boolean) => {
    const iconClass = classNames({
      'bg-center bg-contain bg-no-repeat transition-all': true,
      'cursor-pointer hover:scale-110': !isReminder,
      ' h-[var(--mode-icon-side-a)] aspect-[9/16] bg-[url("/src/assets/mode-portrait-box.png")]':
        orientation === 'portrait',
      'hover:bg-[url("/src/assets/mode-portrait-device.png")]':
        orientation === 'portrait' && !isReminder,
      'w-[var(--mode-icon-side-a)] aspect-video bg-[url("/src/assets/mode-landscape-box.png")]':
        orientation === 'landscape',
      'hover:bg-[url("/src/assets/mode-landscape-device.png")]':
        orientation === 'landscape' && !isReminder,
    });

    if (isReminder)
      return <div className={classNames('self-center', iconClass)} />;

    return (
      <div className="flex flex-col items-center gap-3">
        <div className="flex items-center justify-center h-[var(--mode-icon-side-a)]">
          <div className={iconClass} onClick={() => onClickMode(orientation)} />
        </div>

        {orientation === 'portrait' ? (
          <div className="text-center"> Instagram & Facebook Reels/Stories</div>
        ) : (
          <div className="text-center">YouTube, Vimeo, Websites</div>
        )}
      </div>
    );
  };

  return (
    <Modal show={show} onHide={handleReject} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>
          {videoMode ? 'Reminder' : 'Select video orientation'}
        </Modal.Title>
      </Modal.Header>

      {videoMode ? (
        <Modal.Body>
          <div>
            Your video will be processed and optimised for <i>{videoMode}</i>.
            To confirm, click <b>Continue</b>.
          </div>

          {renderModeIcon(videoMode, true)}

          <Checkbox
            id="mode-checkbox"
            label="Don't show again"
            name="isVideoModeModalSaved"
            checked={isSaved}
            onChange={(e) => setIsSaved(e.target.checked)}
          />

          <div className="flex flex-col sm:flex-row gap-4">
            <Button className="w-full sm:w-1/2" onClick={handleAccept}>
              Continue
            </Button>

            <Button
              colour="pink"
              className="w-full sm:w-1/2"
              onClick={handleReject}
            >
              Cancel Upload
            </Button>
          </div>

          <div className="text-sm italic">
            To change this selection contact us at{' '}
            <a href="mailto:hello@crowdclip.com">hello@crowdclip.com</a>.
          </div>
        </Modal.Body>
      ) : (
        <Modal.Body>
          <div>
            Once selection is made, note that the highlight reel and clips will
            be converted to this orientation and cannot be changed.
          </div>

          {isSettingMode ? (
            <div className="h-[var(--mode-icon-side-a)] flex items-center justify-center">
              <Spinner size="3xl" />
            </div>
          ) : (
            <div className="flex items-center justify-center gap-6">
              {renderModeIcon('portrait')}
              {renderModeIcon('landscape')}
            </div>
          )}
        </Modal.Body>
      )}
    </Modal>
  );
};

export default VideoModeModal;
